/* Pure press/move/release/cancel state machine for the drag-to-look fallback. Some embedded
   browsers never resolve, reject or fire pointerlockerror for requestPointerLock, so a user there
   can click forever and never look around; dragging on the canvas has to work independently of
   whatever pointer lock is doing. inputAdapter.ts is the only caller and wires this to pointer
   events; keeping the decisions here (rather than inline) is what makes them unit-testable
   without a DOM. */

/** Cumulative pixel movement, summed over every move while the pointer is down, below which a
    press+release counts as a click rather than a drag. Matches common desktop/touch tap
    tolerances. */
export const DRAG_CLICK_THRESHOLD_PX = 4;

export type DragPhase = 'idle' | 'active';

export interface DragLookState {
  readonly phase: DragPhase;
  readonly pointerId: number;
  readonly lastX: number;
  readonly lastY: number;
  readonly movedPx: number;
}

/* pointerId is never read while phase is 'idle'; -1 is a sentinel so the state needs no `| null`
   in its consumers. */
export const IDLE_DRAG_LOOK: DragLookState = {
  phase: 'idle',
  pointerId: -1,
  lastX: 0,
  lastY: 0,
  movedPx: 0,
};

export interface DragLookMove {
  readonly state: DragLookState;
  readonly dx: number;
  readonly dy: number;
}

export interface DragLookRelease {
  readonly state: DragLookState;
  readonly wasDrag: boolean;
}

/* A locked pointer already drives look through pointer-lock movement events; starting a drag too
   would double-count the same physical gesture the moment both paths saw it, so a locked pointer
   takes precedence and a press while locked is ignored outright. */
export function pressDragLook(
  state: DragLookState,
  pointerId: number,
  x: number,
  y: number,
  enabled: boolean,
  locked: boolean
): DragLookState {
  if (!enabled || locked || state.phase === 'active') return state;
  return { phase: 'active', pointerId, lastX: x, lastY: y, movedPx: 0 };
}

export function moveDragLook(
  state: DragLookState,
  pointerId: number,
  x: number,
  y: number,
  locked: boolean
): DragLookMove {
  if (locked || state.phase !== 'active' || state.pointerId !== pointerId) {
    return { state, dx: 0, dy: 0 };
  }

  const dx = x - state.lastX;
  const dy = y - state.lastY;
  const next: DragLookState = {
    ...state,
    lastX: x,
    lastY: y,
    movedPx: state.movedPx + Math.hypot(dx, dy),
  };
  return { state: next, dx, dy };
}

export function releaseDragLook(state: DragLookState, pointerId: number): DragLookRelease {
  if (state.phase !== 'active' || state.pointerId !== pointerId) {
    return { state, wasDrag: false };
  }
  return { state: IDLE_DRAG_LOOK, wasDrag: state.movedPx >= DRAG_CLICK_THRESHOLD_PX };
}

export function cancelDragLook(state: DragLookState, pointerId: number): DragLookState {
  if (state.phase !== 'active' || state.pointerId !== pointerId) return state;
  return IDLE_DRAG_LOOK;
}
